import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { LOGO, PHONE, PHONE_HREF } from "@/lib/site";

const SECTIONS = [
  { label: "Services", id: "services" },
  { label: "Gallery", id: "gallery" },
  { label: "Pricing", id: "pricing" },
];

export default function Navbar({ onNavigate }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname === "/") {
      onNavigate(`#${id}`);
    } else {
      sessionStorage.setItem("bac_scroll", id);
      navigate("/");
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname === "/") {
      if (window.__lenis) window.__lenis.scrollTo(0);
      else window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const goBlog = () => {
    setOpen(false);
    navigate("/blog");
  };

  const goCollective = () => {
    setOpen(false);
    navigate("/collective");
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-[100] transition-colors duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="mx-auto max-w-[1400px] px-5 md:px-10 h-20 flex items-center justify-between">
        <button
          onClick={goHome}
          className="flex items-center gap-3 group"
          data-testid="nav-logo-button"
        >
          <img src={LOGO} alt="Black Anchor Collective" className="h-12 w-12 object-contain" />
          <span className="hidden sm:block font-serif text-lg tracking-[0.25em] text-white/90 group-hover:text-gold transition-colors duration-300">
            BLACK ANCHOR
          </span>
        </button>

        <div className="hidden md:flex items-center gap-10">
          {SECTIONS.map((l) => (
            <button
              key={l.id}
              onClick={() => goSection(l.id)}
              className="text-xs uppercase tracking-[0.25em] text-white/60 hover:text-gold transition-colors duration-300"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={goBlog}
            className="text-xs uppercase tracking-[0.25em] text-white/60 hover:text-gold transition-colors duration-300"
            data-testid="nav-link-journal"
          >
            Journal
          </button>
          <button
            onClick={goCollective}
            className="text-xs uppercase tracking-[0.25em] text-gold/90 hover:text-gold transition-colors duration-300"
            data-testid="nav-link-collective"
          >
            Collective
          </button>
        </div>

        <div className="flex items-center gap-4">
          <a
            href={PHONE_HREF}
            className="hidden lg:flex items-center gap-2 text-xs tracking-[0.2em] text-white/70 hover:text-gold transition-colors duration-300"
            data-testid="nav-phone"
          >
            <Phone size={14} /> {PHONE}
          </a>
          <button
            onClick={() => goSection("booking")}
            className="hidden md:inline-flex items-center bg-gold text-black px-6 py-3 text-xs uppercase tracking-[0.25em] hover:bg-gold-light transition-colors duration-300"
            data-testid="nav-get-quote-button"
          >
            Get Quote
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-white p-2"
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {SECTIONS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => goSection(l.id)}
                  className="text-left font-serif text-3xl text-white/80 hover:text-gold transition-colors"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={goBlog}
                className="text-left font-serif text-3xl text-white/80 hover:text-gold transition-colors"
                data-testid="nav-mobile-link-journal"
              >
                Journal
              </button>
              <button
                onClick={goCollective}
                className="text-left font-serif text-3xl text-gold hover:text-gold-light transition-colors"
                data-testid="nav-mobile-link-collective"
              >
                Collective
              </button>
              <button
                onClick={() => goSection("booking")}
                className="text-left font-serif text-3xl text-gold hover:text-gold-light transition-colors"
                data-testid="nav-mobile-link-book"
              >
                Get Quote
              </button>
              <a
                href={PHONE_HREF}
                className="mt-2 inline-flex items-center gap-2 text-gold tracking-[0.2em]"
                data-testid="nav-mobile-phone"
              >
                <Phone size={16} /> {PHONE}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
